import React, { Fragment, useEffect, useMemo, useState } from 'react'
import { getEventsVisitors, getParticipantsList } from '../../../axios/Visitor/VisitorApi';
import { useTranslation } from 'react-i18next';
import { generateTranslateMuiTable } from '../../../utils/Function/commonFunction';
import { StyledDataGrid } from '../../../components/StyledComponents';
import CustomPagination from '../CustomPagination';
import NoRowOverlay from '../../../assets/images/icons/NoRowOverlay';
import CustomColumnsPanel from '../../../components/CustomColumnsPanel';
import DeleteIcon16pxSvg from '../../../assets/images/icons/DeleteIcon16pxSvg';
import RemoveUserDialog from './RemoveUserDialog';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Tooltip } from '@mui/material';
import DetailVisitorDialog from './DetailVisitorDialog';
import { useAuth } from '../../../context/AuthContext';
import dayjs from 'dayjs';
import { format, parseISO } from "date-fns";
import { useLoading } from '../../../context/LoadingContext';

const VisitorTabGridInfoTable = ({ isDateFiltered, selectedDate, searchText, dataInfo, onRowClick, refreshData, onRefresh, onPaginationChange }) => {
    const { t } = useTranslation();
    const [data, setData] = useState([]);
    const [rowCount, setRowCount] = useState(0);
    const [filteredRowCount, setFilteredRowCount] = useState(0);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(50);
    const [removeUserOpen, setRemoveUserOpen] = useState(false);
    const [detailVisitorOpen, setDetailVisitorOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const { genaralSettingDateFormat } = useAuth();
    const [totalPages, setTotalPages] = useState(1);
    const [totalElements, setTotalElements] = useState(0);
    const [show, hide] = useLoading();

    const handleCloseRemoveUser = () => {
        setRemoveUserOpen(false);
    };

    const handleOpenRemoveUser = () => {
        setRemoveUserOpen(true);
    };

    const tableTranslated = useMemo(() => generateTranslateMuiTable(t), [t]);
    const fetchData = async () => {
        const payload = {
            eventId: dataInfo?.id || null,
            pageNo: page + 1,
            recordPerPage: pageSize,
            status: "APPROVED",
            searchText: searchText || null,
            joinDate: isDateFiltered ? selectedDate : null
        };
        try {
            show();
            const response = await getEventsVisitors(payload);

            setData(response?.data?.data || []);

            const total = response?.data?.pagination?.totalPages || 1;

            setTotalPages(total);

            setTotalElements(
                response?.data?.pagination?.totalElements || 0
            );

            // ← thêm
            onPaginationChange?.({
                totalPages: total,
                pageSize,
            });

        } catch (error) {
            console.error(error);
        } finally {
            hide();
        }
    };

    useEffect(() => {
        fetchData();
    }, [dataInfo?.id, page, pageSize, refreshData, searchText, selectedDate]);

    const columnData = [
        { field: 'fullName', headerName: t('common.full-name'), hideable: true, flex: 1, },
        { field: 'citizenNumber', headerName: t('common.personal-id'), hideable: true, flex: 1, },
        { field: 'email', headerName: t('common.email'), hideable: true, flex: 1, },
        { field: 'mobile', headerName: t('common.phone'), hideable: true, flex: 1, },
        {
            field: 'joinDate',
            headerName: t('common.joinedDate'),
            hideable: true,
            flex: 1,
            valueFormatter: (params) => {
                if (!params) return "";

                return format(
                    parseISO(params),
                    genaralSettingDateFormat
                );
            }
        },
        {
            field: 'status',
            headerName: t('common.status'),
            hideable: true,
            flex: 1,
            renderCell: (params) => {
                const status = params.value?.toUpperCase();

                const statusColor = {
                    APPROVED: '#058900',
                    PENDING: '#F97C00',
                    REJECTED: '#D00D00',
                };

                return (
                    <span
                        style={{
                            color: statusColor[status] || '#000',
                            fontWeight: 600,
                        }}
                    >
                        {t(`common.${params.value?.toLowerCase()}`)}
                    </span>
                );
            },
        },
        {
            field: "options",
            headerName: t("common.options"),
            sortable: false,
            filterable: false,
            minWidth: 200,
            renderCell: (params) => (
                <div
                    className="flex gap-2 items-center justify-start h-full"
                    onClick={(e) => e.stopPropagation()}
                >
                    <Tooltip
                        title={t("button.detail")}
                        placement="right"
                        arrow
                    >
                        <button
                            className="p-2 rounded-lg hover:bg-blue-100 transition cursor-pointer flex items-center justify-center"
                            onClick={(e) => {
                                e.stopPropagation();
                                setSelectedRow(params.row);
                                setDetailVisitorOpen(true);
                            }}
                        >
                            <InfoOutlinedIcon
                                className="w-5 h-5 text-[#0057FF]"
                            />
                        </button>
                    </Tooltip>
                    <Tooltip
                        title={t("button.remove")}
                        placement="right"
                        arrow
                    >
                        <button
                            className="p-2 rounded-lg hover:bg-red-100 transition cursor-pointer"
                            onClick={(e) => {
                                e.stopPropagation();
                                setSelectedRow(params.row);
                                handleOpenRemoveUser();
                            }}
                        >
                            <DeleteIcon16pxSvg
                                className="w-5 h-5"
                                color="#D00D00"
                            />
                        </button>
                    </Tooltip>
                </div>
            )
        },
    ];

    const handleRowsPerPageChange = (newPageSize) => {
        // newPageSize ở đây tuỳ CustomPagination, có thể là event hoặc number
        const size =
            typeof newPageSize === "number"
                ? newPageSize
                : parseInt(newPageSize.target.value, 10);

        setPage((prevPage) => {
            // index của record đầu tiên đang xem
            const firstRowIndex = prevPage * pageSize;

            // page mới sao cho vẫn chứa record đó
            const newPage = Math.floor(firstRowIndex / size);

            // phòng trường hợp vượt quá số trang
            const maxPage = Math.max(
                0,
                Math.ceil(filteredRowCount / size) - 1
            );

            return Math.min(newPage, maxPage);
        });

        setPageSize(size);
    };

    const handleRowClick = (params) => {
        const rowData = params.row;
        if (rowData) {
            onRowClick(rowData);
        }
    };

    const hasRows = data.length > 0;
    const gridHeight = hasRows ? 480 : 360;

    return (
        <>
            <Fragment>
                <StyledDataGrid
                    showColumnVerticalBorder
                    sx={{
                        height: gridHeight,

                        "@media (max-width: 1919px)": {
                            height: 750,
                        },

                        ".MuiDataGrid-main": {
                            height: "70vh",
                        },

                        "& .MuiDataGrid-scrollbar--vertical, & .MuiDataGrid-scrollbar--horizontal":
                        {
                            display: "none !important",
                        },

                        "& .MuiDataGrid-scrollbarFiller": {
                            display: "none !important",
                        },
                        "& .MuiDataGrid-columnHeaders": {
                            paddingRight: 0,
                        },
                        "& .MuiDataGrid-topContainer": {
                            paddingRight: 0,
                        },

                        "&": {
                            "--DataGrid-scrollbarSize": "0px",
                        },

                        "& .MuiDataGrid-virtualScroller": {
                            scrollbarWidth: "none",
                        },
                        "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
                            width: 0,
                            height: 0,
                        },
                        "&:hover .MuiDataGrid-virtualScroller, &:focus-within .MuiDataGrid-virtualScroller":
                        {
                            scrollbarWidth: "thin",
                        },
                        "&:hover .MuiDataGrid-virtualScroller::-webkit-scrollbar, &:focus-within .MuiDataGrid-virtualScroller::-webkit-scrollbar":
                        {
                            width: 10,
                            height: 10,
                        },

                        '& [class*="MuiDataGrid-scrollbar"][class*="--horizontal"]': {
                            display: "none !important",
                        },

                        "& input[type=number]::-webkit-inner-spin-button, \
 & input[type=number]::-webkit-outer-spin-button": {
                            WebkitAppearance: "none",
                            margin: 0,
                        },

                        "& input[type=number]": {
                            textAlign: "center",
                            MozAppearance: "textfield",
                            WebkitAppearance: "none",
                            padding: "5px !important",
                            height: "100%",
                            pointerEvents: "none",
                            caretColor: "transparent",
                            userSelect: "none",
                            paddingTop: "8px !important",
                            paddingBottom: "8px !important"
                        },
                    }}
                    rows={data || []}
                    rowCount={rowCount}
                    paginationMode="client"
                    columns={columnData}
                    // loading={gridLoading}
                    getRowId={(r) => r.id}
                    slots={{
                        pagination: CustomPagination,
                        noRowsOverlay: NoRowOverlay,
                        columnsPanel: CustomColumnsPanel,
                    }}
                    slotProps={{
                        pagination: {
                            page,
                            count: rowCount,
                            rowsPerPage: pageSize,
                            onPageChange: setPage,
                            onRowsPerPageChange: handleRowsPerPageChange,
                        },
                    }}
                    localeText={tableTranslated}
                    onRowClick={handleRowClick} />
            </Fragment>

            {removeUserOpen && <RemoveUserDialog id={selectedRow?.id} onClose={handleCloseRemoveUser} onRefresh={onRefresh} refreshData={refreshData} />}
            {detailVisitorOpen && <DetailVisitorDialog id={selectedRow?.id} onClose={() => setDetailVisitorOpen(false)} onRefresh={onRefresh} refreshData={refreshData} />}
        </>
    )
}

export default VisitorTabGridInfoTable